import {
  BracketSeedEntity,
  LolTournamentEntity,
  TournamentMatchGameEntity,
  TournamentPlayerEntity,
  TournamentTeamEntity,
} from 'app/interfaces/entity';
import {
  TournamentBracketParticipantResultEntity,
  TournamentBracketMatchGameEntity,
  TournamentBracketMatchGameStatus,
  TournamentTeamStatus,
} from '@okletsplay/core';
import { Identifier } from 'app/interfaces';
import classNames from 'classnames';
import { ReadMore, Visibility } from '@mui/icons-material';
import { groupBy, difference, isBoolean } from 'lodash';
import { BracketSeedStatus } from 'modules/bracket/components/BracketSeedStatus';
import { BracketTeamCard } from 'modules/bracket/components/BracketTeamCard';
import { BreakTimer } from 'modules/bracket/components/BreakTimer';
import { MatchGameTimer } from 'modules/bracket/components/MatchGameTimer';
import { WinnerBracketSeedCard } from 'modules/bracket/components/WinnerBracketSeedCard';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { CHAMPION_LABEL, SEED_TEAMS_COUNT } from 'modules/bracket/constants';
import { ActiveMatchContext } from 'modules/bracket/providers';
import { MIN_BEST_OF_VALUE } from 'modules/tournaments/constants';
import { currentTournamentSelector } from 'modules/tournaments/store/current-tournament/selectors';
import { IRenderSeedProps, Seed, SingleLineSeed } from 'react-brackets';
import { useSelector } from 'react-redux';
import { useRoundBreakDelay } from 'modules/bracket/hooks/use-round-break-delay';
import { useStyles } from './BracketSeed.styles';
import React, { Fragment, useCallback, useContext, useMemo } from 'react';
import { URL_WITH_PROTOCOL_REGEX } from 'shared/utils';
import { MatchDetailsTabs } from '../TournamentMatchDetailsModal';

const ACTIVE_MATCH_STATUSES = [
  TournamentBracketMatchGameStatus.Ready,
  TournamentBracketMatchGameStatus.Running,
];

export const BracketSeed: (props: IRenderSeedProps) => JSX.Element = ({
  seed,
  rounds,
  roundIndex,
  breakpoint,
}) => {
  const isLineConnector = useMemo(
    () => rounds?.[roundIndex]?.seeds?.length === rounds?.[roundIndex + 1]?.seeds?.length,
    [roundIndex, rounds]
  );
  const { gameBreakDuration } = useRoundBreakDelay(seed?.round, seed?.group);

  const { classes } = useStyles({ isLineConnector });

  const { setActiveMatchId, setActiveTab } = useContext(ActiveMatchContext);

  const tournament = useSelector(currentTournamentSelector.tournament) as LolTournamentEntity;
  const isCurrentUserSpectator = useSelector(currentTournamentSelector.isCurrentUserSpectator);
  const isCurrentUserAdmin = useSelector(currentTournamentSelector.isCurrentUserAdmin);

  const spectateLink = useMemo(() => {
    if (!seed?.status || !tournament) return '';

    const isActive = ACTIVE_MATCH_STATUSES.includes(seed.status);
    const canSpectate = (isCurrentUserAdmin || isCurrentUserSpectator) && isActive;

    if (!canSpectate) return '';

    const sourceKey = 'formattedSpectatorCode';
    const matchGamesWithCodes = (seed as BracketSeedEntity).matchGames.filter(
      (matchGame) => matchGame[sourceKey]
    );

    if (!matchGamesWithCodes || !matchGamesWithCodes.length) return '';

    const activeMatchGame = matchGamesWithCodes.find((matchGame) => {
      return (
        matchGame.status &&
        ![
          TournamentBracketMatchGameStatus.Archived,
          TournamentBracketMatchGameStatus.Completed,
        ].includes(matchGame.status) &&
        !matchGame.isExpired
      );
    });

    if (!activeMatchGame) return '';

    return activeMatchGame[sourceKey];
  }, [isCurrentUserAdmin, isCurrentUserSpectator, seed, tournament]);

  const handleSpectateClick = useCallback(() => {
    if (!spectateLink) return;

    if (!URL_WITH_PROTOCOL_REGEX.test(spectateLink)) {
      setActiveTab(MatchDetailsTabs.Spectate);
      setActiveMatchId(Number(seed?.id) ?? null);
      return;
    }

    window.open(spectateLink, '_blank');
  }, [spectateLink, seed?.id, setActiveMatchId, setActiveTab]);

  const runningMatchGameIndex: number = useMemo(() => {
    const gameIndex = seed.matchGames?.findIndex(
      (matchGame: TournamentBracketMatchGameEntity) =>
        matchGame.status === TournamentBracketMatchGameStatus.Running
    );

    return gameIndex < 0 ? null : gameIndex;
  }, [seed.matchGames]);
  const runningMatchGame = useMemo(() => {
    return seed?.matchGames?.[runningMatchGameIndex];
  }, [runningMatchGameIndex, seed.matchGames]);

  const readyMatchGameIndex: number = useMemo(() => {
    const gameIndex = seed.matchGames?.findIndex(
      (matchGame: TournamentBracketMatchGameEntity) =>
        matchGame.status === TournamentBracketMatchGameStatus.Ready
    );

    return gameIndex < 0 ? 0 : gameIndex;
  }, [seed?.matchGames]);

  const bestOf: number = useMemo(() => {
    return seed.matchGames?.length || tournament.matchSetupSettings?.bestOf || MIN_BEST_OF_VALUE;
  }, [seed, tournament.matchSetupSettings?.bestOf]);

  const isBreakTimerAvailable: boolean = useMemo(() => {
    if (!tournament) {
      return false;
    }

    return ACTIVE_MATCH_STATUSES.includes(seed.status);
  }, [seed.status, tournament]);

  const isRunning: boolean = useMemo(
    () => seed.status === TournamentBracketMatchGameStatus.Running,
    [seed.status]
  );

  const isEmptyMatch = useMemo(() => !seed?.teams?.length, [seed?.teams?.length]);

  const matchResults: Record<Identifier, TournamentBracketParticipantResultEntity[]> = useMemo(
    () =>
      groupBy(
        seed.matchGames?.flatMap(
          (matchGame: TournamentBracketMatchGameEntity) => matchGame?.matchGameResults ?? []
        ),
        'id'
      ),
    [seed]
  );

  const normalizedTeams = useMemo(() => {
    if (seed.teams?.length > 1) {
      return seed.teams;
    }

    return seed.teams.concat(new Array(SEED_TEAMS_COUNT - seed.teams.length).fill(null));
  }, [seed.teams]);

  const readyTeams = useMemo(() => {
    const readyUserIds = seed?.matchGames?.[runningMatchGameIndex]?.readyUsers?.map(
      ({ user }: { user: Identifier }) => user
    );
    return normalizedTeams.reduce((acc, team) => {
      const playersIds =
        team?.players?.map?.((player: TournamentPlayerEntity) => player.user.id) ?? [];
      const isTeamReady = !difference(playersIds, readyUserIds).length;
      return team?.id ? { ...acc, [team.id]: isTeamReady } : { ...acc };
    }, {});
  }, [normalizedTeams, runningMatchGameIndex, seed.matchGames]);

  const matchBracketTitle = useMemo(() => {
    const gameIndex = runningMatchGameIndex || readyMatchGameIndex;
    const matchNumber = Number(seed.id) + 1;

    if (
      seed?.matchGames?.[gameIndex]?.isPlayoffGame &&
      ACTIVE_MATCH_STATUSES.includes(seed.status)
    ) {
      return `Match • Playoff`;
    }

    if (!ACTIVE_MATCH_STATUSES.includes(seed.status)) {
      return `Match #${matchNumber}`;
    }

    if (bestOf > MIN_BEST_OF_VALUE) {
      return `Match #${matchNumber} • Game ${gameIndex} of ${bestOf}`;
    }

    return `Match #${matchNumber}`;
  }, [runningMatchGameIndex, readyMatchGameIndex, seed, bestOf]);

  const isMatchWithExpiredGame = useMemo(() => {
    if (!seed?.matchGames?.length) {
      return false;
    }

    return (
      seed.matchGames.findIndex((matchGame: TournamentMatchGameEntity) => matchGame.isExpired) > -1
    );
  }, [seed.matchGames]);

  const viewDetailsHandler = useCallback(() => {
    setActiveMatchId(Number(seed?.id) ?? null);
  }, [setActiveMatchId, seed?.id]);

  const isGrandFinal = useMemo(
    () => rounds?.find((round) => round.title === CHAMPION_LABEL),
    [rounds]
  );

  const getTeamIdHandler = useCallback(
    (team: string) => {
      if (!team || !runningMatchGame) {
        return false;
      }

      if (!isBoolean(readyTeams[team])) {
        return false;
      }

      return Boolean(readyTeams[team]);
    },
    [runningMatchGame, readyTeams]
  );

  const Wrapper = isLineConnector && !isGrandFinal ? SingleLineSeed : Seed;

  return (
    <Wrapper
      className={classes.seed}
      mobileBreakpoint={breakpoint}
      style={{ padding: '3em 1.5em', minWidth: '352px' }}
    >
      <Box className={classNames(classes.root, { [classes.expiredSeed]: isMatchWithExpiredGame })}>
        <Box position="relative">
          {seed.isFinish ? (
            <WinnerBracketSeedCard team={seed?.teams?.[0] as TournamentTeamEntity} />
          ) : (
            <>
              <Box className={classNames(classes.seedDetails, classes.seedHeader)}>
                <Typography className={classes.bracketTitle}>{matchBracketTitle}</Typography>
                <BracketSeedStatus matchStatus={seed.status} tournamentStatus={tournament.status} />
              </Box>
              {normalizedTeams.map((team, index) => {
                const isDisqualifiedTeam = team?.status === TournamentTeamStatus.Disqualified;
                return (
                  <Fragment key={`${team?.id}-${index}`}>
                    <Box
                      className={classNames(classes.team, {
                        [classes.teamTop]: !index,
                        [classes.teamBottom]: index,
                        [classes.disqualified]: isDisqualifiedTeam,
                      })}
                    >
                      <BracketTeamCard
                        team={team as TournamentTeamEntity}
                        isEmptyMatch={isEmptyMatch}
                        matchResults={matchResults}
                        isReady={getTeamIdHandler(team?.id)}
                        isDisqualifiedTeam={isDisqualifiedTeam}
                        isJoinGameTimerExpired={runningMatchGame?.isJoinGameTimerExpired}
                      />
                    </Box>
                    {!index && <Box className={classes.divider} />}
                  </Fragment>
                );
              })}
              <Box className={classNames(classes.seedDetails, classes.seedFooter)}>
                {isRunning && (
                  <MatchGameTimer
                    startedAt={seed.startedAt}
                    maxGameDuration={tournament.matchSetupSettings?.maxGameDuration}
                  />
                )}
                {isBreakTimerAvailable && (
                  <Box className={classes.timer}>
                    <BreakTimer startedAt={seed.breakStartedAt} duration={gameBreakDuration} />
                  </Box>
                )}
                <Box className={classes.seedDetailsButtons}>
                  {spectateLink && (
                    <Tooltip title="Spectate">
                      <IconButton
                        disableRipple
                        onClick={handleSpectateClick}
                        className={classes.spectateButton}
                      >
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                  )}
                  {Boolean(seed.status) && (
                    <Tooltip title="View Details">
                      <IconButton
                        disableRipple
                        onClick={viewDetailsHandler}
                        className={classes.viewDetailsButton}
                      >
                        <ReadMore />
                        <Typography>Details</Typography>
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Wrapper>
  );
};
